/*
 * SPDX-FileCopyrightText: © 2024 Schneider Electric
 *
 * SPDX-License-Identifier: Apache-2.0
 */

@import url('../shared.css');

:host {
  display: inline-block;
  outline: none;
}

.qds-button:disabled,
.qds-disabled {
  opacity: var(--qds-theme-disabled);
  pointer-events: none;
}

.qds-overflow {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &[data-size='hero'] {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    white-space: normal;
  }
}

.qds-action {
  transform: scaleX(var(--qds-direction-factor, 1));
}

.qds-container {
  display: grid;
  grid-template-areas: 'button';
}

.qds-focus-ring {
  border-radius: max(
      1px,
      var(
        --qds-control-border-radius-top-left,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-top-right,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-bottom-right,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-bottom-left,
        var(--qds-control-border-radius)
      )
    );
  grid-area: button;
  isolation: isolate;
  outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
  outline-offset: var(--qds-focus-border-offset);
  pointer-events: none;
  visibility: hidden;
  width: calc(
    100% + var(--qds-control-border-width) -
      var(
        --qds-control-border-width-inline-end,
        var(--qds-control-border-width)
      )
  );
  height: calc(
    100% + var(--qds-control-border-width) -
      var(--qds-control-border-width-block-end, var(--qds-control-border-width))
  );
}

:host(:focus-visible) .qds-focus-ring {
  visibility: visible;
}

.qds-button {
  position: relative;
  align-items: center;
  appearance: none;

  /* Avoid automatic minimum size on flex element: https://www.w3.org/TR/css-flexbox-1/#min-size-auto */
  min-width: 0;
  background-color: initial;
  border: none;
  border-radius: var(
      --qds-control-border-radius-top-left,
      var(--qds-control-border-radius)
    )
    var(--qds-control-border-radius-top-right, var(--qds-control-border-radius))
    var(
      --qds-control-border-radius-bottom-right,
      var(--qds-control-border-radius)
    )
    var(
      --qds-control-border-radius-bottom-left,
      var(--qds-control-border-radius)
    );
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  grid-area: button;
  justify-content: center;
  outline: none;
  padding-block: 0;
  print-color-adjust: exact;
  text-align: center;
  user-select: none;

  &:any-link {
    text-decoration: none;
  }
}

.qds-icon {
  flex-shrink: 0;
}

.qds-indicator {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  transform: translate(50%, -50%);
}

[data-size='small'] {
  font: var(--qds-control-small-text);

  &.qds-button {
    gap: var(--qds-control-small-gap-internal);
    height: var(--qds-control-small-height);
    padding-inline: var(--qds-control-button-small-padding-horizontal);

    &.qds-icon-only {
      width: var(--qds-control-small-height);
    }
  }

  &.qds-icon {
    font-size: var(--qds-control-small-icon-size);
  }
}

[data-size='standard'] {
  font: var(--qds-control-standard-text);

  &.qds-button {
    gap: var(--qds-control-standard-gap-internal);
    height: var(--qds-control-standard-height);
    padding-inline: var(--qds-control-button-standard-padding-horizontal);

    &.qds-icon-only {
      width: var(--qds-control-standard-height);
    }
  }

  &.qds-icon {
    font-size: var(--qds-control-standard-icon-size);
  }
}

[data-size='large'] {
  font: var(--qds-control-large-text);

  &.qds-button {
    gap: var(--qds-control-large-gap-internal);
    height: var(--qds-control-large-height);
    padding-inline: var(--qds-control-button-large-padding-horizontal);

    &.qds-icon-only {
      width: var(--qds-control-large-height);
    }
  }

  &.qds-icon {
    font-size: var(--qds-control-large-icon-size);
  }
}

[data-size='small'],
[data-size='standard'],
[data-size='large'] {
  line-height: normal;

  &.qds-button.qds-icon-only {
    padding-inline: 0;
  }
}

[data-size='hero'] {
  font: var(--qds-control-hero-text);

  &.qds-button {
    border-radius: var(
        --qds-control-border-radius-top-left,
        var(--qds-control-hero-border-radius)
      )
      var(
        --qds-control-border-radius-top-right,
        var(--qds-control-hero-border-radius)
      )
      var(
        --qds-control-border-radius-bottom-right,
        var(--qds-control-hero-border-radius)
      )
      var(
        --qds-control-border-radius-bottom-left,
        var(--qds-control-hero-border-radius)
      );
    gap: var(--qds-control-hero-gap-internal);
    padding-block: var(--qds-control-hero-padding-auto-height);
    padding-inline: var(--qds-control-button-hero-padding-horizontal);

    &.qds-icon-only {
      height: var(--qds-control-hero-height);
      padding-inline: 0;
      width: var(--qds-control-hero-height);
    }
  }

  &.qds-focus-ring {
    border-radius: max(
        1px,
        var(
          --qds-control-border-radius-top-left,
          var(--qds-control-hero-border-radius)
        )
      )
      max(
        1px,
        var(
          --qds-control-border-radius-top-right,
          var(--qds-control-hero-border-radius)
        )
      )
      max(
        1px,
        var(
          --qds-control-border-radius-bottom-right,
          var(--qds-control-hero-border-radius)
        )
      )
      max(
        1px,
        var(
          --qds-control-border-radius-bottom-left,
          var(--qds-control-hero-border-radius)
        )
      );
  }

  &.qds-icon {
    font-size: var(--qds-control-hero-icon-size);
  }
}

[data-importance='subdued'] {
  color: var(--qds-theme-link-standard-default);

  &.qds-button {
    border-block-end: var(--qds-control-border-width) solid transparent;
    padding-inline: 0;

    &:hover {
      border-block-end: var(--qds-control-border-width) solid
        var(--qds-theme-link-standard-hover);
      color: var(--qds-theme-link-standard-hover);
    }

    &:active {
      border-block-end: var(--qds-control-border-width) solid
        var(--qds-theme-link-standard-pressed);
      color: var(--qds-theme-link-standard-pressed);
    }

    &:not(.qds-icon-only) {
      border-radius: 0;
    }

    &.qds-icon-only {
      border: none;
      color: var(--qds-theme-control-text-standard);

      &:hover {
        background-color: var(--qds-theme-interactive-background-hover);
      }

      &:active {
        background-color: var(--qds-theme-interactive-background-pressed);
      }
    }
  }
}

[data-importance='standard'] {
  color: var(--qds-theme-control-text-standard);

  &.qds-button {
    border-color: var(--qds-theme-control-border);
    border-style: solid;
    border-width: var(--qds-control-border-width)
      var(
        --qds-control-border-width-inline-end,
        var(--qds-control-border-width)
      )
      var(--qds-control-border-width-block-end, var(--qds-control-border-width))
      var(--qds-control-border-width);

    &:hover {
      background-color: var(--qds-theme-interactive-background-hover);
    }

    &:active {
      background-color: var(--qds-theme-interactive-background-pressed);
    }
  }
}

[data-importance='emphasized'] {
  &.qds-button {
    background-color: var(--qds-theme-signature-color-default);
    color: var(--qds-theme-signature-color-contrast);

    &:hover {
      background-color: var(--qds-theme-signature-color-hover);
    }

    &:active {
      background-color: var(--qds-theme-signature-color-pressed);
    }
  }
}

[data-importance='destructive'] {
  &.qds-button {
    background-color: var(--qds-theme-feedback-action-destructive-default);
    color: var(--qds-theme-feedback-action-destructive-contrast);

    &:hover {
      background-color: var(--qds-theme-feedback-action-destructive-hover);
    }

    &:active {
      background-color: var(--qds-theme-feedback-action-destructive-pressed);
    }
  }
}

[data-importance='subdued-destructive'] {
  color: var(--qds-theme-feedback-action-destructive-default);

  &.qds-button {
    border-block-end: var(--qds-control-border-width) solid transparent;
    padding-inline: 0;

    &:hover {
      border-block-end: var(--qds-control-border-width) solid
        var(--qds-theme-feedback-action-destructive-hover);
      color: var(--qds-theme-feedback-action-destructive-hover);
    }

    &:active {
      border-block-end: var(--qds-control-border-width) solid
        var(--qds-theme-feedback-action-destructive-pressed);
      color: var(--qds-theme-feedback-action-destructive-pressed);
    }

    &:not(.qds-icon-only) {
      border-radius: 0;
    }

    &.qds-icon-only {
      border: none;

      &:hover {
        background-color: var(--qds-theme-interactive-background-hover);
        color: var(--qds-theme-feedback-action-destructive-default);
      }

      &:active {
        background-color: var(--qds-theme-interactive-background-pressed);
        color: var(--qds-theme-feedback-action-destructive-default);
      }
    }
  }
}

:is([data-action='dropdown'], [data-action='dropdown-close']) {
  .qds-action {
    margin-inline-start: auto;
    transition-duration: 0.3s;
    transition-property: transform;
  }

  &[data-importance='standard'] {
    background-color: var(--qds-theme-control-input-background);
  }

  &:not([data-importance='subdued'], [data-importance='subdued-destructive']) {
    padding-inline: var(--qds-control-input-padding-horizontal);
  }
}

[data-action='dropdown-close'] .qds-action {
  transform: rotate(-180deg);
}
